<script lang="ts">
	import { onMount, onDestroy } from 'svelte';
	import { afterPaint, markSkeletonVisible } from '$lib/services/sentry';
	import SkeletonBlock from './SkeletonBlock.svelte';

	// General inert replica of the rebrand chrome (HeaderRebrand + FooterRebrand),
	// reusing the same assets (logo SVGs, footer wave path) and geometry as the
	// live components, but with NO anchors, buttons, handlers, or service imports.
	// This keeps the skeleton out of the critical initial bundle while looking
	// authentic. If HeaderRebrand/FooterRebrand visuals change, mirror them here.
	//
	// Owns the `markSkeletonVisible` paint-aligned mark so the skeleton component
	// is self-contained for its visibility timing.
	//
	// The main column renders general funnel zones (coarse title / form-card /
	// CTA shape shared across funnel routes) by default. `routeKey` only drives
	// header logo placement (subscription = single-button centered logo on
	// mobile / left at sm+; default = multi-button left logo) and the
	// `data-skeleton` route root marker asserted by prerender + tests. A consumer
	// can pass slot content to override the default zones for a route that needs
	// a different shape.
	//
	// inert + aria-hidden: visual only, no interaction, excluded from the a11y tree.
	export let routeKey = '';
	const isSubscription = routeKey === 'subscription';
	const year = new Date().getFullYear();
	const footerLegal: string[] = [
		`TM & © 2007-${year} Age of Learning, Inc. All rights reserved.`,
		'Terms & Conditions',
		'Privacy Policy',
		'Do Not Sell My Personal Information',
		'Customer Care',
		'Accessibility Statement',
	];
	let cancelMark: () => void = () => {};

	onMount(() => {
		cancelMark = afterPaint(markSkeletonVisible);
	});
	onDestroy(() => {
		cancelMark();
	});
</script>

<div
	class="rebrand-layout-skeleton"
	aria-hidden="true"
	inert={true}
	data-skeleton={routeKey}
	data-skeleton-part="layout"
>
	<header
		class="sk-header"
		data-skeleton-part="header"
	>
		<div
			class="sk-header__bar"
			class:sk-header__bar--centered={isSubscription}
		>
			<img
				class="sk-header__logo"
				src="/assets-mkt/routes/home/abcmouse-logo-rebrand-horizontal.svg"
				alt=""
			/>
		</div>
	</header>

	<main
		class="sk-main"
		data-skeleton-part="main"
	>
		<div class="sk-main__column">
			{#if $$slots.default}
				<slot />
			{:else}
				<div class="sk-zones">
					<div class="sk-title">
						<SkeletonBlock
							tone="accent"
							height="2.4rem"
							width="70%"
							radius="0.6rem"
						/>
						<SkeletonBlock
							tone="neutral"
							height="1.4rem"
							width="50%"
							radius="0.4rem"
							className="sk-title__sub"
						/>
					</div>
					<SkeletonBlock
						tone="surface"
						height="24rem"
						width="100%"
						radius="1.2rem"
					/>
					<div class="sk-action">
						<SkeletonBlock
							tone="accent"
							height="4.4rem"
							width="100%"
							radius="0.8rem"
						/>
						<SkeletonBlock
							tone="neutral"
							height="1.2rem"
							width="90%"
							radius="0.4rem"
							className="sk-action__terms"
						/>
					</div>
				</div>
			{/if}
		</div>
	</main>

	<footer
		class="sk-footer"
		data-skeleton-part="footer"
	>
		<svg
			class="sk-footer__wave"
			viewBox="0 0 633 36"
			preserveAspectRatio="xMidYMid slice"
			xmlns="http://www.w3.org/2000/svg"
			aria-hidden="true"
		>
			<path
				d="M 0 0 C 66.6667 0 133.3333 0 200 0 C 298 0 334 7 440 6 L 632 6 L 633 36 L 0 36 Z"
				fill="#195AFF"
			/>
		</svg>
		<div class="sk-footer__inner">
			<img
				class="sk-footer__logo"
				src="/assets-mkt/routes/home/abcmouse-logo-rebrand-main-white.svg"
				alt=""
			/>
			<div class="sk-footer__links">
				{#each footerLegal as text, i}
					{#if i > 0}<span class="sk-footer__sep">|</span>{/if}
					<span class="sk-footer__link">{text}</span>
				{/each}
			</div>
			<img
				class="sk-footer__kidsafe"
				src="/assets-mkt/routes/home/kidsafe-2-0-v1.svg"
				alt=""
			/>
		</div>
	</footer>
</div>

<style>
	.rebrand-layout-skeleton {
		display: flex;
		flex-direction: column;
		min-height: 100vh;
		width: 100%;
		background: #fff;
	}
	.sk-header {
		width: 100%;
		border-bottom: 0.3rem solid #e5e7eb;
		background: #fff;
	}
	/* Multi-button layout (default): left logo, px-[2rem] mobile.
	   Matches HeaderRebrand's justify-between bar at max-w-screen-lg (1440px). */
	.sk-header__bar {
		position: relative;
		display: flex;
		align-items: center;
		justify-content: flex-start;
		margin: 0 auto;
		max-width: 144rem;
		padding: 1rem 2rem;
	}
	/* Single-button layout (subscription): centered logo on mobile, left at sm+.
	   Matches HeaderRebrand's flex justify-center + absolute-right mobile layout. */
	.sk-header__bar--centered {
		justify-content: center;
		padding: 1rem 0.8rem;
	}
	.sk-header__logo {
		height: auto;
		width: 100%;
		max-width: 13.1rem;
	}
	@media (min-width: 640px) {
		.sk-header__bar {
			padding: 1.5rem 3.7rem;
		}
		.sk-header__bar--centered {
			justify-content: flex-start;
		}
		.sk-header__logo {
			max-width: 21.7rem;
		}
	}
	/* Main is a centered full-width container; the default funnel zones or the
	   slotted content own their max-widths and horizontal padding (mirrors
	   LayoutRebrand's contentClassList + content wrapper pb-16). */
	.sk-main {
		flex-grow: 1;
		display: flex;
		justify-content: center;
		width: 100%;
		padding-top: 1.2rem;
	}
	.sk-main__column {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
		padding-bottom: 16rem;
	}
	/* General funnel zones: coarse title / form-card / CTA shape shared across
	   funnel routes. Mirrors the resolved funnel forms' max-w-[32rem] sm:max-w-[58rem]
	   md:max-w-[75rem]. A route with a different shape passes slot content. */
	.sk-zones {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1.5rem;
		width: 100%;
		max-width: 32rem;
		margin: 0 auto;
	}
	@media (min-width: 640px) {
		.sk-zones {
			max-width: 58rem;
		}
	}
	@media (min-width: 1200px) {
		.sk-zones {
			max-width: 75rem;
		}
	}
	.sk-title {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
		padding-top: 1rem;
	}
	/* CTA + terms constrained to the resolved CTAButton max-w-[24.6rem]. */
	.sk-action {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1.2rem;
		width: 100%;
		max-width: 24.6rem;
	}
	.sk-footer {
		position: relative;
		display: flex;
		flex-direction: column;
		align-items: center;
		overflow: hidden;
		height: 23rem;
		width: 100%;
		background: #195aff;
	}
	@media (min-width: 640px) {
		.sk-footer {
			height: 36rem;
		}
	}
	.sk-footer__wave {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}
	.sk-footer__inner {
		position: absolute;
		top: 0;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		height: 100%;
		width: 100%;
		padding-top: 2rem;
	}
	.sk-footer__logo {
		height: auto;
		width: 100%;
		max-width: 10.1rem;
		margin: 1rem 0 2rem;
	}
	@media (min-width: 640px) {
		.sk-footer__logo {
			max-width: 18.4rem;
			margin: 4rem 0 4rem;
		}
	}
	.sk-footer__links {
		max-width: 32rem;
		text-align: center;
		color: #fff;
		font-size: 1rem;
		line-height: 1.6;
	}
	@media (min-width: 640px) {
		.sk-footer__links {
			max-width: 58rem;
			font-size: 1.6rem;
		}
	}
	.sk-footer__sep {
		margin-left: 0.4rem;
	}
	.sk-footer__link {
		margin-left: 0.4rem;
	}
	.sk-footer__kidsafe {
		height: auto;
		width: 6.1rem;
		margin-top: 1.5rem;
	}
	@media (min-width: 640px) {
		.sk-footer__kidsafe {
			width: 8.1rem;
			margin-top: 2rem;
		}
	}
</style>
